import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const nf = new Intl.NumberFormat("fr-FR");

const ORDER_STATUS = [
  { v: "pending_payment", label: "En attente paiement" },
  { v: "paid", label: "Payée" },
  { v: "processing", label: "En traitement" },
  { v: "shipped", label: "Expédiée" },
  { v: "delivered", label: "Livrée" },
  { v: "cancelled", label: "Annulée" },
];

function fmtDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
    if (!d) return "-";
    return d.toLocaleString("fr-FR");
  } catch {
    return "-";
  }
}

function money(x) {
  const n = Number(x || 0);
  return `${nf.format(n)} XOF`;
}

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [selected, setSelected] = useState(null);

  const PAGE = 30;

  const load = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const base = collection(db, "orders");
        const q = reset
          ? query(base, orderBy("createdAt", "desc"), limit(PAGE))
          : query(base, orderBy("createdAt", "desc"), startAfter(cursor), limit(PAGE));

        const snap = await getDocs(q);
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (reset) setRows(docs);
        else setRows((prev) => prev.concat(docs));

        const nextCur = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
        setCursor(nextCur);
        setHasMore(Boolean(nextCur) && snap.docs.length === PAGE);
      } finally {
        setLoading(false);
      }
    },
    [cursor, PAGE]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    return rows.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (!t) return true;
      const name = (o?.customer?.name || "").toLowerCase();
      const phone = (o?.customer?.phone || "").toLowerCase();
      const id = (o?.id || "").toLowerCase();
      const payId = (o?.payment?.identifier || "").toLowerCase();
      return name.includes(t) || phone.includes(t) || id.includes(t) || payId.includes(t);
    });
  }, [rows, qText, statusFilter]);

  async function updateOrderStatus(orderId, nextStatus) {
    setBusyId(orderId);
    try {
      await updateDoc(doc(db, "orders", orderId), {
        status: nextStatus,
        updatedAt: new Date(),
      });
      setRows((prev) => prev.map((x) => (x.id === orderId ? { ...x, status: nextStatus } : x)));
      if (selected?.id === orderId) setSelected((s) => ({ ...s, status: nextStatus }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="container py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h3 className="mb-0">Commandes (Admin)</h3>
          <div className="text-muted small">Toutes les commandes</div>
        </div>

        <button className="btn btn-outline-secondary" onClick={() => load(true)} disabled={loading}>
          Rafraîchir
        </button>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-6">
            <label className="form-label">Recherche</label>
            <input
              className="form-control"
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Client / téléphone / orderId / identifier"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Statut</label>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tous</option>
              {ORDER_STATUS.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3 text-md-end">
            <div className="small text-muted">Résultats : {filtered.length}</div>
          </div>
        </div>
      </div>

      {loading && rows.length === 0 && <div className="alert alert-info">Chargement…</div>}
      {!loading && filtered.length === 0 && <div className="alert alert-light border">Aucune commande.</div>}

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Total</th>
                    <th>Statut</th>
                    <th>Paiement</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const totals = o?.totals || {};
                    const pay = o?.payment || {};
                    return (
                      <tr key={o.id} onClick={() => setSelected(o)} style={{ cursor: "pointer" }}>
                        <td className="small">{fmtDate(o.createdAt)}</td>
                        <td>
                          <div className="fw-semibold">{o?.customer?.name || "-"}</div>
                          <div className="small text-muted">{o?.customer?.phone || "-"}</div>
                        </td>
                        <td className="fw-semibold">{money(totals.totalXof ?? o.total)}</td>
                        <td>
                          <span className="badge text-bg-secondary">{o.status || "-"}</span>
                        </td>
                        <td>
                          <div className="small">
                            {pay.provider || "-"} / {pay.status || "-"}
                          </div>
                          <div className="small text-muted">{(pay.identifier || "").slice(0, 18)}…</div>
                        </td>
                        <td className="text-end">
                          <select
                            className="form-select form-select-sm"
                            value={o.status || "pending_payment"}
                            disabled={busyId === o.id}
                            onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {ORDER_STATUS.map((s) => (
                              <option key={s.v} value={s.v}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-3 d-flex justify-content-between">
              <div className="small text-muted">Total chargé : {rows.length}</div>
              <button className="btn btn-outline-primary btn-sm" disabled={!hasMore || loading} onClick={() => load(false)}>
                Charger plus
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card p-3">
            {!selected ? (
              <div className="text-muted">Clique une commande pour voir le détail.</div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold">Commande #{selected.id}</div>
                    <div className="small text-muted">{fmtDate(selected.createdAt)}</div>
                  </div>
                  <span className="badge text-bg-dark">{selected.status}</span>
                </div>

                <hr />

                <div className="mb-2 fw-semibold">Client</div>
                <div className="small">
                  <div>{selected?.customer?.name}</div>
                  <div>{selected?.customer?.phone}</div>
                  <div className="text-muted">
                    {selected?.customer?.address} — {selected?.customer?.city}
                  </div>
                </div>

                <hr />

                <div className="mb-2 fw-semibold">Montants</div>
                <div className="small">
                  <div className="d-flex justify-content-between">
                    <span>Sous-total produits</span>
                    <span>{money(selected?.totals?.itemsTotalXof ?? 0)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Frais PayGate</span>
                    <span>{money(selected?.totals?.paygateFeeXof ?? selected?.payment?.fees?.amountXof ?? 0)}</span>
                  </div>
                  <div className="d-flex justify-content-between fw-semibold">
                    <span>Total</span>
                    <span>{money(selected?.totals?.totalXof ?? 0)}</span>
                  </div>
                </div>

                <hr />

                <div className="mb-2 fw-semibold">Articles</div>
                <div className="list-group">
                  {(selected?.items || []).map((it, idx) => (
                    <div key={idx} className="list-group-item">
                      <div className="fw-semibold">{it.name}</div>
                      <div className="small text-muted">
                        {it.qty} × {money(it.priceXof)} ={" "}
                        <span className="fw-semibold">{money(it.lineTotalXof)}</span>
                      </div>
                      <div className="small text-muted">Vendeur: {it.vendorId || "-"}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
